import { useContext, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import SearchableMenu from "components/searchable-menu"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "connected-components/dashboard/analytics"
import { useAddModule } from "connected-components/dashboard/hooks/use-add-module"
import { MODULE_TYPES } from "connected-components/dashboard/modules/types"
import {
  buildSecondaryModuleConfigFromMenuItem,
  SECONDARY_MODULE_CONFIG_VIEW_KEYS,
} from "connected-components/dashboard/helpers/module-renderers"
import {
  getOpenInNewTabMergeOptions,
  mergeItemsWithOrderAndSecondaryAction,
} from "connected-components/dashboard/components/view-selectors/assets-menu-items"
import { useContentSetsMetadata } from "resources/content-sets"
import { OK } from "helpers/data-statuses"

export const CONTENT_SET_TEMPLATE_PREFIX = "content-set-"

const BASE_MENU_ITEMS = [
  {
    id: "performance",
    label: "Performance",
    groupId: "model",
    groupLabel: "Risk model",
  },
  {
    id: "risk",
    label: "Risk",
    groupId: "model",
    groupLabel: "Risk model",
  },
  {
    id: "exposure",
    label: "Exposure",
    groupId: "model",
    groupLabel: "Risk model",
  },
  {
    id: "factor-sensitivity-predicted",
    label: "Predicted Factor Sensitivity",
    groupId: "factor-sensitivity",
    groupLabel: "Factor Sensitivity",
  },
  {
    id: "factor-sensitivity-historical",
    label: "Historical Factor Sensitivity",
    groupId: "factor-sensitivity",
    groupLabel: "Factor Sensitivity",
  },
]

function buildContentSetMenuItems(contentSetsMetadata, contentSetsStatus) {
  if (contentSetsStatus !== OK || !contentSetsMetadata?.length) return []
  return contentSetsMetadata.map(contentSet => ({
    id: `${CONTENT_SET_TEMPLATE_PREFIX}${contentSet.id}`,
    label: contentSet.name,
    groupId: contentSet.proprietary ? "proprietary" : "licensed",
    groupLabel: contentSet.proprietary ? "Proprietary" : "Licensed",
  }))
}

export default function GraphTemplateSelector({ context, disablePortal = false }) {
  const { selectedTemplate, setSelectedTemplate } = useContext(context)
  const [contentSetsMetadata, contentSetsStatus] = useContentSetsMetadata()
  const analytics = useAnalytics()
  const addModule = useAddModule()

  const handleOpenInNewTab = useCallback((item, event) => {
    analytics.track(DASHBOARD_EVENTS.GRAPH_OPENED_AS_TAB, { templateId: item.id })
    const config = buildSecondaryModuleConfigFromMenuItem(
      item,
      event,
      SECONDARY_MODULE_CONFIG_VIEW_KEYS.INITIAL_VIEW,
    )
    addModule({ type: MODULE_TYPES.GRAPH, config: { ...config, selectedTemplate: item.id } })
  }, [analytics, addModule])

  const rawMenuItems = useMemo(() => (
    [...BASE_MENU_ITEMS, ...buildContentSetMenuItems(contentSetsMetadata, contentSetsStatus)]
  ), [contentSetsMetadata, contentSetsStatus])

  const menuItems = useMemo(() => (
    mergeItemsWithOrderAndSecondaryAction([rawMenuItems], getOpenInNewTabMergeOptions(handleOpenInNewTab))
  ), [rawMenuItems, handleOpenInNewTab])

  const handleTemplateSelect = useCallback((selectedItem) => {
    analytics.track(DASHBOARD_EVENTS.GRAPH_TEMPLATE_SELECTED, {
      templateId: selectedItem.id,
      templateName: selectedItem.label,
    })
    setSelectedTemplate(selectedItem.id)
  }, [analytics, setSelectedTemplate])

  return <div className="graph-template-selector">
    <SearchableMenu
      items={menuItems}
      current={selectedTemplate}
      onSelect={handleTemplateSelect}
      disablePortal={disablePortal}
    />
  </div>
}

GraphTemplateSelector.propTypes = {
  context: PropTypes.object.isRequired,
  disablePortal: PropTypes.bool,
}
